import Box from '@mui/material/Box';
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { OnboardDevices } from './OnboardDevices';
import { Tab, Tabs } from '@mui/material';
import { hasAllowedRole } from '../utils/hasAllowedRole';
import { UserRoles } from '../interfaces';
import { ROUTES_CONFIGS } from '../config';
import { useCognitoUser } from '../hooks/useCognitoUser';
import { OnboardDevicesHardwareConfigurations } from './OnboardDevicesHardwareConfigurations';
import { OnboardDevicesOnline } from './OnboardDevicesOnline';
import { OnboardDevicesRouteCards } from './OnboardDevicesRouteCards';
import { ProtectedRoute } from '../Components/ProtectedRoute';
import {
    ROLES_WITH_DEVICE_VIEW,
    ROLES_WITH_ROUTE_CARD_ACCESS,
    ROLES_WITH_HARDWARE_CONFIG_ACCESS,
    ROLES_WITH_ONLINE_PEERS_ACCESS,
} from '../permissions';

export const Devices = () => {
    const [tabValue, setTabValue] = useState<number | null>(0);
    const { userRoles } = useCognitoUser();
    const navigate = useNavigate();

    const routes = useMemo(() => {
        if (userRoles && hasAllowedRole(userRoles, [UserRoles.ADMINS])) {
            return [
                ROUTES_CONFIGS.ONBOARD_DEVICES,
                ROUTES_CONFIGS.ONBOARD_DEVICES_HARDWARE,
                ROUTES_CONFIGS.ONBOARD_DEVICES_ONLINE,
                ROUTES_CONFIGS.ONBOARD_DEVICES_ROUTE_CARDS,
            ];
        } else if (userRoles && hasAllowedRole(userRoles, [UserRoles.OPERATOR, UserRoles.ENGINEER])) {
            return [ROUTES_CONFIGS.ONBOARD_DEVICES, ROUTES_CONFIGS.ONBOARD_DEVICES_ROUTE_CARDS];
        }

        return [];
    }, [userRoles]);

    useEffect(() => {
        const activeTabIndex = routes.findIndex((route) => location.pathname.split('/').pop() === route.path);
        if (activeTabIndex !== -1) {
            setTabValue(activeTabIndex);
        }
    }, [location.pathname, routes]);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        navigate(`/app/devices/${routes[newValue].path}`);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Box sx={{ borderBottom: 1, p: 2, pb: 0, borderColor: 'divider', flexShrink: 0 }}>
                <Tabs value={tabValue} onChange={handleChange}>
                    {routes.map((route) => (
                        <Tab label={route.label} key={route.path} />
                    ))}
                </Tabs>
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Routes>
                    <Route
                        path="onboard-devices"
                        element={
                            <ProtectedRoute allowedRoles={ROLES_WITH_DEVICE_VIEW}>
                                <OnboardDevices />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="onboard-devices-hardware"
                        element={
                            <ProtectedRoute allowedRoles={ROLES_WITH_HARDWARE_CONFIG_ACCESS}>
                                <OnboardDevicesHardwareConfigurations />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="onboard-devices-online"
                        element={
                            <ProtectedRoute allowedRoles={ROLES_WITH_ONLINE_PEERS_ACCESS}>
                                <OnboardDevicesOnline />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="onboard-devices-route-cards"
                        element={
                            <ProtectedRoute allowedRoles={ROLES_WITH_ROUTE_CARD_ACCESS}>
                                <OnboardDevicesRouteCards />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<Navigate to="onboard-devices" replace />} />
                </Routes>
            </Box>
        </Box>
    );
};
